"use client"

import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { installConsumerEdgeBack } from "../lib/consumer-edge-back.ts"
import { consumerBackFallback, hasConsumerBack, installConsumerHistory } from "../lib/consumer-history.ts"
import { isConsumerAppPath, isConsumerTabPath, replaceConsumerTab } from "../lib/consumer-navigation.ts"
import { nativeApp } from "../lib/native-app.ts"
import { isNativeApp, nativePlatform } from "../lib/native-capabilities.ts"
import { registerNativePush } from "../lib/native-session-client.ts"
import { safeNextRoute } from "../lib/safe-route.ts"

export const closeTopmostDialog = (root: ParentNode = document): boolean => {
  const dialogs = Array.from(root.querySelectorAll<HTMLDialogElement>("dialog[open]"))
  const dialog = dialogs.at(-1)
  if (dialog === undefined) return false
  const cancel = new Event("cancel", { bubbles: false, cancelable: true })
  if (dialog.dispatchEvent(cancel)) dialog.close()
  return true
}

export const NativeWebViewBridge = () => {
  const router = useRouter()
  const pathname = usePathname()
  const [exitNotice, setExitNotice] = useState<string | null>(null)
  useEffect(() => installConsumerHistory(), [])
  useEffect(() => {
    if (!isNativeApp()) return
    let active = true
    let running = false
    let sent = false
    let retries = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const sync = () => {
      if (!active || running || nativePlatform() !== "IOS" || !nativeApp.supports("setBackSwipe")) return
      // WKWebView's native history gesture can ignore SPA entries even while
      // canGoBack and allowsBackForwardNavigationGestures are true. Web owns
      // the edge gesture so it follows the same history policy as header Back.
      if (sent) return
      running = true
      void nativeApp
        .setBackSwipe(false)
        .then(() => {
          sent = true
        })
        .catch(() => undefined)
        .finally(() => {
          running = false
          if (active && retries++ < 25) timer = setTimeout(sync, 200)
        })
    }
    const changed = () => {
      retries = 0
      sync()
    }
    const ready = () => {
      sent = false
      changed()
    }
    window.addEventListener("cashhub:native-ready", ready)
    const removeEdgeBack = installConsumerEdgeBack(
      () => sent && nativePlatform() === "IOS",
      () => window.__cashhubHandleNativeBack?.() === true,
    )
    sync()
    return () => {
      active = false
      clearTimeout(timer)
      removeEdgeBack()
      window.removeEventListener("cashhub:native-ready", ready)
    }
  }, [])
  useEffect(() => {
    if (!isNativeApp() || pathname === "/login") return
    let active = true
    let running = false
    const syncPush = (force = false): void => {
      if (!active || running || document.visibilityState === "hidden") return
      running = true
      void registerNativePush(force)
        .catch(() => {
          /* Push is optional; retry on resume, token refresh or the next authenticated page. */
        })
        .finally(() => {
          running = false
        })
    }
    const stop = (): void => {
      active = false
    }
    const start = (): void => {
      active = true
      syncPush(true)
    }
    const update = (): void => syncPush(true)
    window.addEventListener("cashhub:native-ready", update)
    window.addEventListener("cashhub:native-push-changed", update)
    window.addEventListener("cashhub:session-ready", start)
    window.addEventListener("cashhub:session-clearing", stop)
    window.addEventListener("online", update)
    document.addEventListener("visibilitychange", update)
    const unsubscribe = nativeApp.subscribe((event) => {
      if (event.event === "lifecycleChanged" && event.data.state === "resumed") syncPush(true)
    })
    syncPush()
    return () => {
      unsubscribe()
      active = false
      window.removeEventListener("cashhub:native-ready", update)
      window.removeEventListener("cashhub:native-push-changed", update)
      window.removeEventListener("cashhub:session-ready", start)
      window.removeEventListener("cashhub:session-clearing", stop)
      window.removeEventListener("online", update)
      document.removeEventListener("visibilitychange", update)
    }
  }, [pathname])
  useEffect(() => {
    let armedAt: number | null = null
    let timer: ReturnType<typeof setTimeout> | undefined
    let closing = false
    let returning = false
    let active = true
    const reset = () => {
      armedAt = null
      clearTimeout(timer)
      setExitNotice(null)
    }
    reset()
    const notice = (message: string) => {
      clearTimeout(timer)
      setExitNotice(message)
      timer = setTimeout(reset, 2_000)
    }
    const handleBack = (): boolean => {
      if (closeTopmostDialog()) {
        reset()
        return true
      }
      const currentPath = window.location.pathname
      if (isConsumerAppPath(currentPath) && !isConsumerTabPath(currentPath)) {
        if (returning) return true
        returning = true
        reset()
        if (hasConsumerBack()) router.back()
        else router.replace(consumerBackFallback(currentPath), { scroll: false })
        return true
      }
      if (pathname === null || !isConsumerTabPath(window.location.pathname) || nativePlatform() !== "ANDROID") {
        reset()
        return false
      }
      if (closing) return true
      const now = performance.now()
      if (armedAt !== null && now - armedAt < 2_000) {
        reset()
        closing = true
        void nativeApp.close().catch(() => {
          if (!active) return
          closing = false
          notice("앱을 종료하지 못했어요. 다시 시도해 주세요.")
        })
      } else {
        armedAt = now
        notice("한 번 더 누르면 종료됩니다")
      }
      return true
    }
    const visibility = () => {
      if (document.visibilityState === "hidden") reset()
    }
    document.addEventListener("visibilitychange", visibility)
    window.addEventListener("pagehide", reset)
    window.addEventListener("cashhub:navigation-start", reset)
    window.__cashhubHandleNativeBack = handleBack
    const navigate = (path: string, mode: "auto" | "replace" = "auto"): boolean => {
      const destination = safeNextRoute(path)
      if (destination === null || !["auto", "replace"].includes(mode)) return false
      if (replaceConsumerTab(destination)) return true
      if (new URL(destination, window.location.origin).pathname !== window.location.pathname) window.dispatchEvent(new Event("cashhub:navigation-start"))
      if (mode === "replace" || isConsumerTabPath(new URL(destination, window.location.origin).pathname)) router.replace(destination, { scroll: false })
      else router.push(destination)
      return true
    }
    window.__cashhubNavigate = navigate
    const unsubscribe = nativeApp.subscribe((event) => {
      if (event.event === "lifecycleChanged" && event.data.state !== "resumed") reset()
    })
    return () => {
      unsubscribe()
      active = false
      clearTimeout(timer)
      document.removeEventListener("visibilitychange", visibility)
      window.removeEventListener("pagehide", reset)
      window.removeEventListener("cashhub:navigation-start", reset)
      if (window.__cashhubHandleNativeBack === handleBack) delete window.__cashhubHandleNativeBack
      if (window.__cashhubNavigate === navigate) delete window.__cashhubNavigate
    }
  }, [pathname, router])

  return exitNotice === null ? null : (
    <output aria-live="polite" className="app-exit-notice">
      {exitNotice}
    </output>
  )
}
